/**
 * System Repository
 * COMPLETE implementation for system entities (Users, Notifications, KPIs, Sync)
 */

import { BaseRepository } from '../BaseRepository';
import { db } from '../db';
import type { User, Notification, KPI, PerformanceMetric, SyncItem } from '../../../types/database';

export class UserRepository extends BaseRepository<User> {
  constructor() {
    super(db.users, 'users');
  }

  async findByUsername(username: string): Promise<User | undefined> {
    return await this.table.where('username').equals(username).first();
  }

  async findByEmail(email: string): Promise<User | undefined> {
    return await this.table.where('email').equals(email).first();
  }

  async findByRole(role: User['role']): Promise<User[]> {
    return await this.table.where('role').equals(role).toArray();
  }

  async findByStatus(status: User['status']): Promise<User[]> {
    if (!status) return [];
    return await this.table.where('status').equals(status).toArray();
  }

  async getActive(): Promise<User[]> {
    return await this.findByStatus('active');
  }

  async search(searchTerm: string): Promise<User[]> {
    const term = searchTerm.toLowerCase();
    return await this.table.filter(user =>
      (user.username !== undefined && user.username.toLowerCase().includes(term)) ||
      user.email.toLowerCase().includes(term) ||
      (user.firstName !== undefined && user.firstName.toLowerCase().includes(term)) ||
      (user.lastName !== undefined && user.lastName.toLowerCase().includes(term))
    ).toArray();
  }
}

export class NotificationRepository extends BaseRepository<Notification> {
  constructor() {
    super(db.notifications, 'notifications');
  }

  async findByUser(userId: string): Promise<Notification[]> {
    return await this.table.where('userId').equals(userId).toArray();
  }

  async findUnread(userId: string): Promise<Notification[]> {
    return await this.table
      .where('userId')
      .equals(userId)
      .filter(n => !n.read)
      .toArray();
  }

  async findByType(type: Notification['type']): Promise<Notification[]> {
    return await this.table.where('type').equals(type).toArray();
  }

  async findByPriority(priority: Notification['priority']): Promise<Notification[]> {
    if (!priority) return [];
    return await this.table.where('priority').equals(priority).toArray();
  }

  async getUnreadCount(userId: string): Promise<number> {
    return await this.table
      .where('userId')
      .equals(userId)
      .filter(n => !n.read)
      .count();
  }

  async markAllAsRead(userId: string): Promise<number> {
    const notifications = await this.findUnread(userId);
    const now = new Date();
    
    const updates = notifications.map(n => ({
      ...n,
      read: true,
      readAt: now,
    }));

    await this.table.bulkPut(updates);
    return updates.length;
  }

  async deleteOld(days: number = 30): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - days);

    const oldNotifications = await this.table
      .where('createdAt')
      .below(cutoffDate)
      .toArray();

    const ids = oldNotifications.map(n => n.id);
    await this.table.bulkDelete(ids);
    return ids.length;
  }
}

export class KPIRepository extends BaseRepository<KPI> {
  constructor() {
    super(db.kpis, 'kpis');
  }

  async findByCategory(category: string): Promise<KPI[]> {
    return await this.table.where('category').equals(category).toArray();
  }

  async findByPeriod(period: string): Promise<KPI[]> {
    return await this.table.where('period').equals(period).toArray();
  }

  async findByName(kpiName: string): Promise<KPI[]> {
    return await this.table.where('kpiName').equals(kpiName).toArray();
  }
}

export class PerformanceMetricRepository extends BaseRepository<PerformanceMetric> {
  constructor() {
    super(db.performanceMetrics, 'performanceMetrics');
  }

  async findByCategory(category: string): Promise<PerformanceMetric[]> {
    return await this.table.where('category').equals(category).toArray();
  }

  async findByName(metricName: string): Promise<PerformanceMetric[]> {
    return await this.table.where('metricName').equals(metricName).toArray();
  }

  async findByDateRange(startDate: Date, endDate: Date): Promise<PerformanceMetric[]> {
    return await this.table
      .where('date')
      .between(startDate, endDate, true, true)
      .toArray();
  }
}

export class SyncQueueRepository extends BaseRepository<SyncItem> {
  constructor() {
    super(db.syncQueue, 'syncQueue');
  }

  async findByEntity(entity: string): Promise<SyncItem[]> {
    return await this.table.where('entity').equals(entity).toArray();
  }

  async findByStatus(status: SyncItem['status']): Promise<SyncItem[]> {
    if (!status) return [];
    return await this.table.where('status').equals(status).toArray();
  }

  async getPending(): Promise<SyncItem[]> {
    return await this.findByStatus('pending');
  }

  async getFailed(): Promise<SyncItem[]> {
    return await this.findByStatus('failed');
  }

  async markAsSyncing(id: string): Promise<void> {
    await this.update(id, { status: 'syncing' as const });
  }

  async markAsCompleted(id: string): Promise<void> {
    await this.update(id, { status: 'completed' as const });
  }

  async markAsFailed(id: string, error: string): Promise<void> {
    const item = await this.findById(id);
    if (item) {
      await this.update(id, {
        status: 'failed' as const,
        retryCount: (item.retryCount || 0) + 1,
        error,
      });
    }
  }

  async clearCompleted(olderThanDays: number = 7): Promise<number> {
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - olderThanDays);

    const oldItems = await this.table
      .where('status')
      .equals('completed')
      .filter(item => item.timestamp !== undefined && new Date(item.timestamp) < cutoffDate)
      .toArray();

    const ids = oldItems.map(i => i.id);
    await this.table.bulkDelete(ids);
    return ids.length;
  }
}

export const userRepository = new UserRepository();
export const notificationRepository = new NotificationRepository();
export const kpiRepository = new KPIRepository();
export const performanceMetricRepository = new PerformanceMetricRepository();
export const syncQueueRepository = new SyncQueueRepository();


